import { localDayKey, timeZoneOffsetMinutes } from './day';

/**
 * What the connectivity banner says: nothing (`online`), the connection has been gone for a
 * while (`offline`), it is back and the app is catching up (`reconnecting`), or it has caught up
 * (`back-online`, shown briefly).
 */
export type ConnectivityState = 'online' | 'offline' | 'reconnecting' | 'back-online';

/** A drop shorter than this is never shown. */
export const OFFLINE_AFTER_MS = 3_000;
/** How long "back online" stays up. */
export const BACK_ONLINE_FOR_MS = 3_000;
/** Quiet time with nothing fetching, after something loaded, before the app counts as caught up. */
export const SETTLE_MS = 500;

export interface ConnectivityInput {
  online: boolean;
  /** Anything is being fetched or flushed. */
  busy: boolean;
  /** When the newest data in the cache was loaded. */
  loadedAt?: number;
}

export interface Connectivity {
  readonly state: ConnectivityState;
  update(input: ConnectivityInput): void;
  subscribe(listener: () => void): () => void;
  dispose(): void;
}

export function createConnectivity(): Connectivity {
  let state: ConnectivityState = 'online';
  const listeners = new Set<() => void>();
  let offlineTimer: ReturnType<typeof setTimeout> | undefined;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectedAt = 0;

  const set = (next: ConnectivityState) => {
    if (next === state) return;
    state = next;
    for (const listener of listeners) listener();
  };
  const stopSettling = () => {
    clearTimeout(settleTimer);
    settleTimer = undefined;
  };
  const stopHiding = () => {
    clearTimeout(hideTimer);
    hideTimer = undefined;
  };
  const stopOffline = () => {
    clearTimeout(offlineTimer);
    offlineTimer = undefined;
  };

  return {
    get state() {
      return state;
    },
    update({ online, busy, loadedAt = 0 }) {
      if (!online) {
        stopSettling();
        stopHiding();
        if (state !== 'online') {
          stopOffline();
          set('offline');
        } else if (offlineTimer === undefined) {
          offlineTimer = setTimeout(() => {
            offlineTimer = undefined;
            set('offline');
          }, OFFLINE_AFTER_MS);
        }
        return;
      }
      stopOffline();
      if (state === 'online' || state === 'back-online') return;
      if (state === 'offline') reconnectedAt = Date.now();
      set('reconnecting');
      if (busy || loadedAt < reconnectedAt) {
        stopSettling();
      } else if (settleTimer === undefined) {
        settleTimer = setTimeout(() => {
          settleTimer = undefined;
          set('back-online');
          hideTimer = setTimeout(() => {
            hideTimer = undefined;
            set('online');
          }, BACK_ONLINE_FOR_MS);
        }, SETTLE_MS);
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      stopOffline();
      stopSettling();
      stopHiding();
      listeners.clear();
    },
  };
}

/**
 * "10:42am" for a load earlier today, "14 Oct" for an earlier day, in the reader's locale.
 * `timeZone` defaults to the device's.
 */
export function formatLoadedAt(
  loadedAt: number,
  now: Date,
  locale: string,
  timeZone?: string,
): string {
  const loaded = new Date(loadedAt);
  const dayOf = (date: Date) =>
    localDayKey(date, timeZone ? timeZoneOffsetMinutes(timeZone, date) : undefined);
  const sameDay = dayOf(loaded) === dayOf(now);
  return new Intl.DateTimeFormat(
    locale,
    sameDay
      ? { hour: 'numeric', minute: '2-digit', timeZone }
      : { day: 'numeric', month: 'short', timeZone },
  ).format(loaded);
}
